/**
 * Alert envelope — distinct from EventEnvelope (per FLYsafe MQTT Topic doc).
 * Alerts are the Alert-Router's curated, ack-able output on
 * `v1/{tenant}/aircraft/{serial}/alerts[/{severity}]` + `shared/alerts/broadcast`.
 * They carry their own id, title, source event, and ack lifecycle — none of
 * which the raw event stream has. Drives the Live Alerts pane.
 */
import { z } from 'zod';

export const AlertSeverity = z.enum(['critical', 'warning', 'info']);

// Doc alert categories are DAA | C2 | Power | OI | System; kept permissive so
// an unexpected value doesn't drop the alert. TISB / Operational Assets added
// per the BE job/category mapping (2026-08-13) — see schemas/events.ts.
export const AlertCategory = z.enum([
  'DAA',
  'C2',
  'Power',
  'OI',
  'System',
  'Comms',
  'Mission',
  'Surveillance',
  'Infrastructure',
  'TISB',
  'Operational Assets',
]);

export const AlertEnvelope = z.object({
  alert_id: z.string(),
  drone_id: z.string().nullable(), // null for broadcast (tenant-wide) alerts
  timestamp: z.string().datetime(),
  severity: AlertSeverity,
  category: AlertCategory,
  title: z.string(),
  message: z.string(),
  source_event: z.string().nullable().optional(),
  requires_ack: z.boolean().default(false),
  acked_at: z.string().nullable().optional(),
  acked_by: z.string().nullable().optional(),
  /** See `EventEnvelope.contexts`. */
  contexts: z.array(z.string()).default([]),
  /** See `EventEnvelope.source_tenant` — same partner-provenance field,
   *  same caveats. */
  source_tenant: z.string().nullable().optional(),
  /** `sourceEvent: "waypoint_pred"` fields (ENG-86, confirmed via
   *  simulator test against SIMTL70, 2026-09-16 — see the ticket for the
   *  captured payload). Present only on that source_event; every other
   *  alert leaves these null. `waypoint_index` is the wire's `waypointIndex`
   *  verbatim — its base (0- vs 1-indexed) and whether it lines up 1:1 with
   *  `OiLiveEnvelope.waypoints`' array order is NOT confirmed by BE, so
   *  consumers must cross-check it against `target_lat`/`target_lng` rather
   *  than trust it as a raw array offset (see `lib/waypoints/progress.ts`).
   *  `sourceEvent: "waypoint_approached"` alerts, on the real sandbox
   *  broker as of 2026-09-16, carry none of these fields — the waypoint
   *  index there is only in the free-text `message`, which is not parsed
   *  out of it. We've since asked BE (ENG-40 ticket, 2026-09-24) to add
   *  `waypointIndex` to `waypoint_approached` too; this same field parses
   *  it the moment BE ships it, no schema change needed — see
   *  `useWaypointProgressStore.applyAlert`, which already trusts it
   *  directly when present, and `progress.ts`'s module header. */
  waypoint_index: z.number().int().nonnegative().nullable().optional(),
  distance_m: z.number().nullable().optional(),
  eta_seconds: z.number().nullable().optional(),
  current_lat: z.number().nullable().optional(),
  current_lng: z.number().nullable().optional(),
  target_lat: z.number().nullable().optional(),
  target_lng: z.number().nullable().optional(),
  /** `waypoint_approached` only (ENG-211, confirmed live on sandbox
   *  2026-10-06). `lap` is the route loop count from 0 — on a looping route the
   *  same `waypoint_index` returns with a higher lap. */
  lap: z.number().int().nonnegative().nullable().optional(),
  /** `waypoint_approached` only: every waypoint reached on the current mission,
   *  oldest first, capped at 500 by BE — so on a looping route it spans MANY
   *  laps (26 in the capture); filter to the alert's own `lap` before use.
   *  Since 2026-10-06 each entry also carries the closest approach and BE's own
   *  `missed` verdict (ENG-211) — use that flag, don't re-derive it. */
  reached_waypoints: z
    .array(
      z.object({
        waypoint_index: z.number().int().nonnegative(),
        lap: z.number().int().nonnegative(),
        /** ISO-8601 UTC, normalised (BE sends microseconds). */
        reached_at: z.string().datetime(),
        /** Closest approach when it counted as reached (metres). Null on a BE
         *  that predates the field. */
        distance_m: z.number().nullable().optional(),
        /** `true` when `distance_m` was above BE's missed threshold: the drone
         *  passed it but never got there. Null on a BE that predates the field. */
        missed: z.boolean().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  /** `waypoint_approached` only: BE's verdict for the waypoint just reached
   *  (the same flag the latest `reached_waypoints` entry carries). */
  missed: z.boolean().nullable().optional(),
  /** The distance BE compared against (20 m at the time of writing; BE says it
   *  is configurable). Informational — the UI uses `missed`, not this. */
  missed_threshold_m: z.number().nullable().optional(),
});

export type AlertSeverity = z.infer<typeof AlertSeverity>;
export type AlertCategory = z.infer<typeof AlertCategory>;
export type AlertEnvelope = z.infer<typeof AlertEnvelope>;
